import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus } from '@enums/campaign.enum';
import { ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { ICtaResult } from '@interfaces/campaign-analytics.interface';
import { EDetailLevel } from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { Observable } from 'rxjs';
import {
  EManualMatchingStatus,
  EManualMatchingStatusAction,
  IManualMatchingStatus,
  IMatchingActionsVisibleParams,
  IMatchingSubheaderVisibleParams,
} from './manual-cta-result-matching-type.interface';

export const ACTION_STATUS_MAP: Record<
  EManualMatchingStatusAction.markAsMatched | EManualMatchingStatusAction.markAsUnmatched,
  EManualMatchingStatus
> = {
  [EManualMatchingStatusAction.markAsMatched]: EManualMatchingStatus.matched,
  [EManualMatchingStatusAction.markAsUnmatched]: EManualMatchingStatus.unmatched,
};

@Injectable({
  providedIn: 'root',
})
export class ManualMatchingStatusService {
  private readonly http = inject(HttpClient);

  public computeMatchingLabelKey(cta: ICtaResult | ICtaResultApi, bestMatchedCtaId: string | null): string {
    if (cta.matchingType === EMatchingType.bonus) {
      return 'pages.campaignDetails.ctaAnalytics.details.deliverables.matching.bonus';
    }

    if (cta.matchingType === EMatchingType.matched) {
      return bestMatchedCtaId === cta.ctaId
        ? 'pages.campaignDetails.ctaAnalytics.details.deliverables.matching.matchedBestResult'
        : 'pages.campaignDetails.ctaAnalytics.details.deliverables.matching.matched';
    }

    return 'pages.campaignDetails.ctaAnalytics.details.deliverables.matching.unrelated';
  }

  public computeCampaignIsLive(
    campaignStatus: ECampaignStatus,
    campaignProcessStatus: EProcessStatus,
    socialContentId: string | null | undefined,
  ): boolean {
    return (
      (campaignStatus === ECampaignStatus.live || campaignProcessStatus === EProcessStatus.processing) &&
      !!socialContentId
    );
  }

  public computeActionsVisible(params: IMatchingActionsVisibleParams): boolean {
    if (params.adminMode || params.creatorBriefingMode) {
      return false;
    }

    if (params.exportMode) {
      const exp = params.campaignExport;

      if (!exp?.showDeliverableStatus || exp?.detailLevel !== EDetailLevel.detailedAnalytics) {
        return false;
      }
    }

    return params.campaignIsLive && params.campaignProcessStatus === EProcessStatus.processing;
  }

  public computeSubheaderVisible(params: IMatchingSubheaderVisibleParams): boolean {
    if (params.creatorBriefingMode) {
      return false;
    }

    if (params.exportMode) {
      const exp = params.campaignExport;

      return !!exp?.showDeliverableStatus && exp?.detailLevel !== EDetailLevel.generalInfo;
    }

    return true;
  }

  public getManualMatchingStatuses(params?: HttpParams): Observable<IApiResponse<IManualMatchingStatus[]>> {
    return this.http.get<IApiResponse<IManualMatchingStatus[]>>(`/manual-matching-status`, { params });
  }

  public getManualMatchingStatusesExport(
    exportId: string,
    params?: HttpParams,
  ): Observable<IApiResponse<IManualMatchingStatus[]>> {
    return this.http.get<IApiResponse<IManualMatchingStatus[]>>(`/manual-matching-status/export/${exportId}`, {
      params,
    });
  }

  public getManualMatchingStatusesBriefing(
    briefingId: string,
    params?: HttpParams,
  ): Observable<IApiResponse<IManualMatchingStatus[]>> {
    return this.http.get<IApiResponse<IManualMatchingStatus[]>>(
      `/manual-matching-status/briefing/creator/${briefingId}`,
      { params },
    );
  }

  public createManualMatchingStatus(data: IManualMatchingStatus): Observable<IManualMatchingStatus> {
    return this.http.post<IManualMatchingStatus>(`/manual-matching-status`, data);
  }

  public updateManualMatchingStatus(id: string, data: IManualMatchingStatus): Observable<IManualMatchingStatus> {
    return this.http.put<IManualMatchingStatus>(`/manual-matching-status/${id}`, data);
  }
}
